import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { APIError, client } from './apiClient';
import { User, transformRawUser } from '../types/api';
import { setUserContext } from './sentry';

const USER_CACHE_TIME = 5 * 60 * 1000; // 5 minutes

export interface UseCurrentUserResult {
  currentUser: User | undefined;
  isLoading: boolean;
  error: Error | null;
  isError: boolean;
  refetch: ReturnType<typeof useQuery<unknown, Error, User | null>>['refetch'];
  isFetching: boolean;
  isSuccess: boolean;
}

export function useCurrentUser(): UseCurrentUserResult {
  const { data, isLoading, error, isError, refetch, isFetching, isSuccess } = useQuery<
    unknown,
    Error,
    User | null
  >({
    queryKey: ['currentUser'],
    queryFn: async () => {
      try {
        const response = await client.userSession();

        if (!response.user) {
          return null;
        }

        setUserContext(response.user.id);
        return transformRawUser(response.user);
      } catch (error) {
        // A 401 just means "not logged in". Resolve to a "no user" state rather
        // than propagating an error — throwing would put the query into an
        // error/retry cycle on public pages (e.g. profiles) that legitimately
        // render for anonymous visitors. Re-throw anything else.
        if (error instanceof APIError && error.status === 401) {
          return null;
        }
        throw error;
      }
    },
    staleTime: USER_CACHE_TIME,
  });

  if (data && !isLoading && !isError) {
    setUserContext(data.id);
  }
  return {
    currentUser: data ?? undefined,
    isLoading,
    error,
    isError,
    refetch,
    isFetching,
    isSuccess,
  };
}

export async function isAuthenticated(): Promise<boolean> {
  try {
    const response = await client.userSession();
    return !!response.user;
  } catch {
    return false;
  }
}

/** Persist EU cookie-notice dismissal via POST /data/user/seen_cookie_notice. */
export function useMarkCookieNoticeSeen() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (formKey: string) => client.seenCookieNotice(formKey),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: ['currentUser'] });
      const previousUser = queryClient.getQueryData<User | null>(['currentUser']);
      queryClient.setQueryData<User | null>(['currentUser'], (old) => {
        if (!old) return old;
        return { ...old, seenCookieNotice: true };
      });
      return { previousUser };
    },
    onError: (_err, _formKey, context) => {
      if (context?.previousUser !== undefined) {
        queryClient.setQueryData(['currentUser'], context.previousUser);
      }
    },
  });
}
